import { Button } from '../../../components/Button';
import type { BillingPlan, BillingStripeSubscription } from '../../../types/adminBilling';
import {
  COLLECTION_METHOD_LABEL,
  instantCell,
  subscriptionAmountText,
  subscriptionStatusNote,
  subscriptionStatusText,
} from './billingFormat';

interface BillingStripeSubscriptionSectionProps {
  /**
   * Null until the live read answers, which is also what "the read has not started yet" looks like.
   * The section treats both the same way on purpose — see the branch below — so it needs no
   * separate loading flag.
   */
  subscription: BillingStripeSubscription | null;
  error: string | null;
  /**
   * The plan's cached copy of the same four facts, shown only while the live read is outstanding
   * and labelled as what we last heard. Stripe is authoritative for a subscription that exists, so
   * this never overrides `subscription`.
   */
  plan: BillingPlan | null;
  /** Something else on the panel is in flight, so nothing here may be pressed. */
  busy: boolean;
  /** Which cancel is currently running, so only that button reads as busy. */
  cancelling: 'periodEnd' | 'now' | null;
  creating: boolean;
  onCreate: () => void;
  onRequestCancel: (atPeriodEnd: boolean) => void;
  onRetry: () => void;
}

/**
 * Statuses that say the subscription is over for good, in Stripe's own words.
 *
 * Used **only to take the cancel controls away**, which is the safe direction for the one pair of
 * buttons here that cannot be undone: a status this build does not recognise leaves them on offer,
 * and the API refuses a second cancel with a sentence rather than doing anything.
 */
const ENDED_STATUSES = new Set(['canceled', 'incomplete_expired']);

/**
 * The Stripe subscription: whether there is one, what it does, and how to stop it.
 *
 * **A customer is not a subscription.** Linking or creating a customer hands billing to Stripe;
 * until a subscription exists Stripe raises nothing on a schedule and neither does this system, so
 * the organization owes nobody anything. That is a state an operator cannot see from anywhere else
 * on the screen, which is why this section says it in words rather than leaving a blank panel.
 *
 * **The status is Stripe's own word**, so it matches the Stripe dashboard. `trialing` is glossed,
 * because on its own it reads as a free trial when what it actually means here is "starts on the
 * date you set".
 */
export const BillingStripeSubscriptionSection = ({
  subscription,
  error,
  plan,
  busy,
  cancelling,
  creating,
  onCreate,
  onRequestCancel,
  onRetry,
}: BillingStripeSubscriptionSectionProps) => {
  const status = subscription?.status ?? null;
  const ended =
    subscription?.canceledAt !== null && subscription?.canceledAt !== undefined
      ? true
      : ENDED_STATUSES.has((status ?? '').trim().toLowerCase());
  const note = subscriptionStatusNote(status);

  return (
    <div className="billing-stripe__subscription" data-testid="billing-stripe-subscription">
      <h4 className="billing-panel__subtitle">Recurring invoice</h4>

      {error ? (
        <div className="admin-error" role="alert">
          <span data-testid="billing-stripe-subscription-load-error">{error}</span>
          <Button
            variant="secondary"
            onClick={onRetry}
            data-testid="billing-stripe-subscription-retry"
          >
            Try again
          </Button>
        </div>
      ) : /*
          No answer yet, whether or not the read has actually started. It is deliberately not
          `loading && !subscription`: between the customer arriving and the read being kicked off
          there is a moment with neither, and treating that as "there is no subscription" put the
          create button and "nothing raises invoices" in front of an operator whose organization may
          well have one. A subscription already in hand is left on screen while it refreshes.
        */
      !subscription ? (
        <>
          <p className="billing-panel__note" data-testid="billing-stripe-subscription-loading">
            Reading the subscription from Stripe...
          </p>
          {plan?.stripeSubscriptionId && (
            /*
              The plan's cache, named as a cache. It is what this system last heard, and on a screen
              that is about to be told the current answer it would be misleading to present it as
              Stripe speaking now.
            */
            <p className="billing-panel__note" data-testid="billing-stripe-subscription-hint">
              Last known: {subscriptionStatusText(plan.stripeSubscriptionStatus)}
              {plan.stripeCollectionMethod
                ? `, ${COLLECTION_METHOD_LABEL[plan.stripeCollectionMethod].toLowerCase()}`
                : ''}
              {plan.stripeSubscriptionNextInvoiceAt
                ? `, next invoice ${instantCell(plan.stripeSubscriptionNextInvoiceAt)}`
                : ''}
              .
            </p>
          )}
        </>
      ) : subscription.hasSubscription ? (
        <>
          <dl className="billing-panel__facts">
            <div className="billing-panel__fact">
              <dt className="billing-panel__fact-label">Status at Stripe</dt>
              <dd
                className="billing-panel__fact-value"
                data-testid="billing-stripe-subscription-status"
              >
                {subscriptionStatusText(status)}
              </dd>
            </div>
            <div className="billing-panel__fact">
              <dt className="billing-panel__fact-label">Stripe bills</dt>
              <dd
                className="billing-panel__fact-value"
                data-testid="billing-stripe-subscription-amount"
              >
                {subscriptionAmountText(
                  subscription.amountCents,
                  subscription.currency,
                  subscription.interval
                )}
              </dd>
            </div>
            <div className="billing-panel__fact">
              <dt className="billing-panel__fact-label">Collected by</dt>
              <dd
                className="billing-panel__fact-value"
                data-testid="billing-stripe-subscription-collection"
              >
                {subscription.collectionMethod
                  ? COLLECTION_METHOD_LABEL[subscription.collectionMethod]
                  : 'Not known'}
                {subscription.daysUntilDue !== null
                  ? `, due ${subscription.daysUntilDue} days later`
                  : ''}
              </dd>
            </div>
            <div className="billing-panel__fact">
              <dt className="billing-panel__fact-label">Next invoice</dt>
              <dd
                className="billing-panel__fact-value"
                data-testid="billing-stripe-subscription-next"
              >
                {subscription.nextInvoiceAt ? instantCell(subscription.nextInvoiceAt) : 'None due'}
              </dd>
            </div>
            {subscription.startsAt && (
              <div className="billing-panel__fact">
                <dt className="billing-panel__fact-label">Billing starts</dt>
                <dd
                  className="billing-panel__fact-value"
                  data-testid="billing-stripe-subscription-starts"
                >
                  {instantCell(subscription.startsAt)}
                </dd>
              </div>
            )}
            <div className="billing-panel__fact">
              <dt className="billing-panel__fact-label">Stripe subscription id</dt>
              <dd
                className="billing-panel__fact-value"
                data-testid="billing-stripe-subscription-id"
              >
                {subscription.subscriptionId ?? '-'}
              </dd>
            </div>
          </dl>

          {subscription.message && (
            <p
              className="billing-panel__note"
              data-testid="billing-stripe-subscription-message"
            >
              {subscription.message}
            </p>
          )}

          {note && (
            <p
              className="billing-panel__reminder-reason"
              data-testid="billing-stripe-subscription-note"
            >
              {note}
            </p>
          )}

          {subscription.cancelAtPeriodEnd && !ended && (
            <div className="billing-panel__banner billing-panel__banner--warning">
              <p
                className="billing-panel__reminder-outcome"
                data-testid="billing-stripe-subscription-ending"
              >
                Set to end when this period finishes
                {subscription.cancelAt ? ` on ${instantCell(subscription.cancelAt)}` : ''}. Stripe
                raises no invoice after that.
              </p>
            </div>
          )}

          {!ended && (
            <div className="billing-panel__actions">
              {!subscription.cancelAtPeriodEnd && (
                <Button
                  variant="secondary"
                  onClick={() => onRequestCancel(true)}
                  loading={cancelling === 'periodEnd'}
                  disabled={busy}
                  data-testid="billing-stripe-cancel-period-end"
                >
                  Stop at the end of this period
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={() => onRequestCancel(false)}
                loading={cancelling === 'now'}
                disabled={busy}
                data-testid="billing-stripe-cancel-now"
              >
                End it immediately
              </Button>
            </div>
          )}
        </>
      ) : (
        <>
          <p className="billing-panel__note" data-testid="billing-stripe-no-subscription">
            {subscription?.message ||
              'There is no Stripe subscription for this organization yet.'}
          </p>
          <p className="billing-panel__note">
            Nothing raises invoices until one exists. Stripe bills nothing on a schedule without a
            subscription, and this system raises nothing for an organization Stripe manages.
          </p>
          <div className="billing-panel__actions">
            <Button
              onClick={onCreate}
              loading={creating}
              disabled={busy}
              data-testid="billing-stripe-create-subscription"
            >
              Create the subscription
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
